/**
 * One-time purge of the rogue service worker + poisoned auth cache.
 *
 * --- Why this exists ---------------------------------------------------------
 * A previous build registered a Workbox service worker with a
 * `StaleWhileRevalidate` runtime-caching rule for the Supabase Auth API.
 * Workbox runtime caches are keyed by URL only (the Authorization header is
 * ignored), so a cached `GET user` response for one identity was served to
 * another: after switching teacher → demo student, switching back installed
 * the teacher's tokens but the user object came from the cache (demo user),
 * producing a hybrid session that survived reloads.
 *
 * The caching rule has been removed from `vite.config.ts`, so fresh installs
 * are safe — but browsers that already have the old worker + cache entries
 * keep serving stale auth responses until both are gone. This module runs
 * once per browser (gated by a localStorage flag) at startup: it unregisters
 * every service worker and drops every CacheStorage entry. The current build
 * then registers its own (rule-free) worker via `virtual:pwa-register`.
 *
 * The purge is fire-and-forget and bounded: a slow browser must never block
 * app boot on cache cleanup.
 */

export const SW_AUTH_PURGE_FLAG = "curio:sw-auth-cache-purge-v1";

const PURGE_TIMEOUT_MS = 2_000;

export type PurgeDeps = {
  serviceWorker?: {
    // Note: the DOM lib types getRegistrations() as returning a *readonly*
    // array — the dep type must say readonly too, or the real
    // ServiceWorkerContainer is not assignable.
    getRegistrations: () => Promise<readonly { unregister: () => Promise<unknown> }[]>;
  };
  caches?: {
    keys: () => Promise<string[]>;
    delete: (key: string) => Promise<unknown>;
  };
  storage?: {
    getItem: (key: string) => string | null;
    setItem: (key: string, value: string) => void;
  };
};

function defaultDeps(): PurgeDeps {
  return {
    serviceWorker: typeof navigator !== "undefined" ? navigator.serviceWorker : undefined,
    caches: typeof caches !== "undefined" ? caches : undefined,
    storage: typeof localStorage !== "undefined" ? localStorage : undefined,
  };
}

/**
 * Unregisters all service workers and clears all caches, exactly once per
 * browser. Safe to call on every boot; subsequent calls are no-ops.
 */
export async function purgeRogueServiceWorkerCachesOnce(
  deps: PurgeDeps = defaultDeps()
): Promise<void> {
  const storage = deps.storage;
  try {
    if (storage?.getItem(SW_AUTH_PURGE_FLAG) === "done") return;
  } catch {
    // Storage unreadable — still attempt the purge, just without the flag.
  }

  const tasks: Promise<unknown>[] = [];

  if (deps.serviceWorker?.getRegistrations) {
    tasks.push(
      deps.serviceWorker
        .getRegistrations()
        .then((registrations) =>
          Promise.all(
            registrations.map((registration) => registration.unregister().catch(() => undefined))
          )
        )
        .catch(() => undefined)
    );
  }

  if (deps.caches?.keys) {
    tasks.push(
      deps.caches
        .keys()
        .then((keys) =>
          Promise.all(keys.map((key) => deps.caches!.delete(key).catch(() => undefined)))
        )
        .catch(() => undefined)
    );
  }

  if (tasks.length > 0) {
    await Promise.race([
      Promise.all(tasks),
      new Promise<void>((resolve) => setTimeout(resolve, PURGE_TIMEOUT_MS)),
    ]);
  }

  try {
    storage?.setItem(SW_AUTH_PURGE_FLAG, "done");
  } catch {
    // Non-fatal: the purge itself already ran; worst case we retry next boot.
  }
}
